import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { supabase } from '@/lib/customSupabaseClient';
import {
    ArrowLeft, Package, DollarSign, BarChart, Info, Calendar, Clock,
    CheckCircle, XCircle, Code, List, Hash, Tag, Copy
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

const DataField = ({ icon: Icon, label, value, children }) => {
    if (value === null || value === undefined || value === '') return null;
    return (
        <div className="flex items-start gap-3">
            <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
            <div className="flex-grow min-w-0">
                <p className="text-xs font-medium text-muted-foreground">{label}</p>
                {children || <p className="text-sm font-medium text-foreground">{value}</p>}
            </div>
        </div>
    );
};

const LoadingSkeleton = () => (
    <div className="space-y-6">
        <div className="flex items-center gap-4 mb-6">
            <Skeleton className="h-9 w-9 rounded-md" />
            <div className="space-y-2">
                <Skeleton className="h-7 w-64" />
                <Skeleton className="h-4 w-48" />
            </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
                <Card>
                    <CardHeader><Skeleton className="h-5 w-32" /></CardHeader>
                    <CardContent className="space-y-3">
                        <Skeleton className="h-48 w-full" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-3/4" />
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader><Skeleton className="h-5 w-32" /></CardHeader>
                    <CardContent className="space-y-3">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-full" />
                    </CardContent>
                </Card>
            </div>
            <div className="space-y-6">
                {Array.from({ length: 3 }).map((_, i) => (
                    <Card key={i}>
                        <CardHeader><Skeleton className="h-5 w-28" /></CardHeader>
                        <CardContent className="space-y-3">
                            <Skeleton className="h-4 w-full" />
                            <Skeleton className="h-4 w-3/4" />
                            <Skeleton className="h-4 w-1/2" />
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    </div>
);

const ProgramDetail = () => {
    const { id } = useParams();
    const [program, setProgram] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchProgram = async () => {
            setLoading(true);
            setError(null);
            try {
                const { data, error } = await supabase
                    .from('admin_programs_v')
                    .select('*')
                    .eq('id', id)
                    .single();

                if (error) throw error;
                if (!data) throw new Error('Program not found.');

                setProgram(data);
            } catch (err) {
                setError('Failed to fetch program details.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchProgram();
        }
    }, [id]);

    const handleCopyId = () => {
        if (program?.id) {
            navigator.clipboard.writeText(program.id)
                .then(() => {
                    toast.success("Copied to clipboard!", { description: "Program ID has been copied." });
                })
                .catch(err => {
                    toast.error("Failed to copy", { description: "Could not copy Program ID to clipboard." });
                    console.error("Failed to copy program ID: ", err);
                });
        }
    };

    if (loading) {
        return (
            <>
                <Helmet><title>Loading... - My Movement Space</title></Helmet>
                <LoadingSkeleton />
            </>
        );
    }

    if (error || !program) {
        return (
            <>
                <Helmet><title>Error - My Movement Space</title></Helmet>
                <div className="space-y-4">
                    <div className="flex items-center gap-4">
                        <Button asChild variant="outline" size="icon">
                            <Link to="/programs"><ArrowLeft className="h-4 w-4" /></Link>
                        </Button>
                        <h1 className="text-2xl font-semibold tracking-tight">Program Not Found</h1>
                    </div>
                    <Card>
                        <CardContent className="py-10 text-center">
                            <p className="text-sm text-destructive">{error || 'Program not found.'}</p>
                            <Button asChild variant="outline" className="mt-4">
                                <Link to="/programs">Back to Programs</Link>
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </>
        );
    }

    return (
        <>
            <Helmet>
                <title>{program.name} - My Movement Space</title>
                <meta name="description" content={`Detailed view of the ${program.name} program.`} />
            </Helmet>

            <div className="mb-6 flex items-center gap-4">
                <Button asChild variant="outline" size="icon" className="flex-shrink-0">
                    <Link to="/programs">
                        <ArrowLeft className="h-4 w-4" />
                    </Link>
                </Button>
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight">{program.name}</h1>
                    <p className="text-xs text-muted-foreground font-mono flex items-center gap-1.5 mt-0.5">
                        ID: {program.id}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleCopyId}
                            className="h-5 w-5 text-muted-foreground hover:text-foreground"
                        >
                            <Copy className="h-3 w-3" />
                        </Button>
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Program Info</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {program.image_url ? (
                                <img
                                    src={program.image_url}
                                    alt={`Image for ${program.name}`}
                                    className="w-full h-auto object-cover rounded-lg border"
                                />
                            ) : (
                                <div className="w-full h-48 bg-muted flex items-center justify-center rounded-lg text-muted-foreground text-sm">
                                    No image available
                                </div>
                            )}
                            <p className="text-sm text-muted-foreground">{program.description || 'No description provided.'}</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader>
                            <CardTitle>Stripe Details</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4 font-mono text-sm">
                            <DataField icon={Tag} label="Stripe Product ID" value={program.stripe_product_id || 'Not set'} />
                            <DataField icon={Tag} label="Stripe Price ID" value={program.stripe_price_id || 'Not set'} />
                        </CardContent>
                    </Card>
                </div>
                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Key Details</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <DataField icon={DollarSign} label="Price">
                                <p className="text-sm font-semibold text-foreground">{program.price ? `${program.price.toFixed(2)} ${program.currency}` : 'Free'}</p>
                            </DataField>
                            <DataField icon={program.is_active ? CheckCircle : XCircle} label="Status">
                                <Badge variant={program.is_active ? 'success' : 'destructive'}>
                                    {program.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                            </DataField>
                            <DataField icon={BarChart} label="Difficulty" value={program.difficulty} />
                            <DataField icon={Calendar} label="Duration" value={`${program.duration_weeks} weeks`} />
                            <DataField icon={Info} label="Target Condition" value={program.target_condition || 'N/A'} />
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader>
                            <CardTitle>Program Contents</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <DataField icon={Hash} label="Total Sessions" value={program.sessions_count} />
                            <DataField icon={Hash} label="Total Unique Exercises" value={program.exercises_count} />
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader>
                            <CardTitle>Timestamps</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <DataField icon={Calendar} label="Created At" value={new Date(program.created_at).toLocaleString()} />
                            <DataField icon={Calendar} label="Last Updated" value={new Date(program.updated_at).toLocaleString()} />
                        </CardContent>
                    </Card>
                </div>
            </div>
        </>
    );
};

export default ProgramDetail;
